import { INews } from '@/@types/News';
import defaultBanner from '@/assets/bg-finished.png';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Megaphone } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NewsBannerProps {
  news: INews[];
}

const LIMIT = 140;

function SlideWithBanner({ item }: { item: INews }) {
  const { i18n } = useTranslation('dashboard');
  const [expanded, setExpanded] = useState(false);
  const lang = i18n.language || 'pt';
  const title = item.title_translations?.[lang] || item.title;
  const description = item.description_translations?.[lang] || item.description;
  const isLong = description.length > LIMIT;

  return (
    <div className="relative h-36 sm:h-40 w-full overflow-hidden">
      <img
        src={item.banner_url!}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { (e.target as HTMLImageElement).src = defaultBanner; }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/70 to-zinc-950/10" />
      <div className="relative z-10 h-full flex flex-col justify-center gap-1.5 px-5 max-w-[75%] sm:max-w-[65%]">
        <h3 className="text-white font-semibold text-sm sm:text-base leading-snug line-clamp-1">{title}</h3>
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={expanded ? 'e' : 'c'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`text-zinc-300 text-xs sm:text-sm leading-relaxed ${!expanded && isLong ? 'line-clamp-3' : ''} [&_h1]:text-lg [&_h1]:font-bold [&_h2]:text-base [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-bold [&_b]:font-bold [&_strong]:font-bold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:hover:underline`}
            dangerouslySetInnerHTML={{ __html: expanded || !isLong ? description : `${description.slice(0, LIMIT)}...` }}
          />
        </AnimatePresence>
        {isLong && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-primary text-xs font-semibold hover:opacity-75 transition-opacity self-start"
          >
            {expanded ? 'Ver menos ↑' : 'Ver mais ↓'}
          </button>
        )}
      </div>
    </div>
  );
}

function SlideNoBanner({ item }: { item: INews }) {
  const { i18n } = useTranslation('dashboard');
  const [expanded, setExpanded] = useState(false);
  const lang = i18n.language || 'pt';
  const title = item.title_translations?.[lang] || item.title;
  const description = item.description_translations?.[lang] || item.description;
  const isLong = description.length > LIMIT;

  return (
    <div className="flex items-start gap-4 px-5 py-4">
      <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/15 border border-primary/30 shrink-0 mt-0.5">
        <Megaphone className="w-4 h-4 text-primary" />
      </span>
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <p className="text-white font-semibold text-sm leading-snug">{title}</p>
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={expanded ? 'e' : 'c'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`text-zinc-400 text-xs sm:text-sm leading-relaxed ${!expanded && isLong ? 'line-clamp-3' : ''} [&_h1]:text-lg [&_h1]:font-bold [&_h2]:text-base [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-bold [&_b]:font-bold [&_strong]:font-bold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:hover:underline`}
            dangerouslySetInnerHTML={{ __html: expanded || !isLong ? description : `${description.slice(0, LIMIT)}...` }}
          />
        </AnimatePresence>
        {isLong && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-primary text-xs font-semibold hover:opacity-75 transition-opacity self-start mt-0.5"
          >
            {expanded ? 'Ver menos ↑' : 'Ver mais ↓'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function NewsBanner({ news }: NewsBannerProps) {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  if (!news.length) return null;

  const total = news.length;

  const paginate = (dir: number) => {
    setDirection(dir);
    setCurrent((prev) => (prev + dir + total) % total);
  };

  const variants = {
    enter: (d: number) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  const item = news[current];
  const hasBanner = !!item.banner_url;

  return (
    <div className="w-full border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/95 md:bg-zinc-950/70 md:backdrop-blur-sm">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/80">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 max-md:hidden" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
          <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Novidades</span>
        </div>

        {total > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => paginate(-1)}
              className="flex items-center justify-center w-6 h-6 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-zinc-500 tabular-nums min-w-[3ch] text-center">
              {current + 1}/{total}
            </span>
            <button
              onClick={() => paginate(1)}
              className="flex items-center justify-center w-6 h-6 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="relative overflow-hidden">
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={current}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {hasBanner ? <SlideWithBanner item={item} /> : <SlideNoBanner item={item} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
